import { useState } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { Pagination, Button, Box } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { useGetOrganizationProductContentsQuery } from '../features/api/rest/apiProductContentsSlice';
import Error from './Error';
import Spinner from './Spinner';
import ProductOrganizationBoardCard from './ProductOrganizationBoardCard';
import ProductOrganizationBoardDialog from './ProductOrganizationBoardDialog';

function ProductOrganizationBoard({ opk, ean, permission, searchParamsName = '' }) {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const modifiedSearchParamsName = searchParamsName ? `${searchParamsName}_` : '';
  const contentsForPage = parseInt(searchParams.get(`${modifiedSearchParamsName}psize`), 10) || 10;
  const contentPage = parseInt(searchParams.get(`${modifiedSearchParamsName}page`), 10) || 1;
  const ordering = searchParams.get(`${modifiedSearchParamsName}ordering`) || '-id';
  const [isAdding, setIsAdding] = useState(false);
  const { contents, contentsCount, isLoading, isFetching, isError } = useGetOrganizationProductContentsQuery(
    { opk, ean, ordering, page: contentPage, psize: contentsForPage },
    {
      selectFromResult: ({ data }) => ({
        contents: data?.results,
        contentsCount: data?.count,
      }),
    }
  );
  const handlePageChange = (page) => {
    searchParams.set(`${modifiedSearchParamsName}page`, page);
    navigate(`${pathname}?${searchParams.toString()}`);
  };

  const pagination = contentsCount > contentsForPage && <Pagination sx={{ my: 2 }} count={Math.ceil(contentsCount / contentsForPage)} page={contentPage} onChange={(event, newPage) => handlePageChange(newPage)} />;

  if (isLoading || isFetching) return <Spinner />;

  if (isError) return <Error />;

  return (
    <>
      {permission() && (
        <Button sx={{ mt: 2 }} startIcon={<AddIcon />} type="submit" onClick={() => setIsAdding(true)} variant="contained" color="primary">
          {t('organizations.product.board.new')}
        </Button>
      )}

      {pagination}
      <Box>
        {contents &&
          contents.map((content) => {
            return <ProductOrganizationBoardCard key={content.id} id={content.id} content={content.content} isPublic={content.public} permission={permission} />;
          })}
      </Box>
      {pagination}
      <ProductOrganizationBoardDialog ean={ean} actionType="add" isOpen={isAdding} setIsOpen={setIsAdding} />
    </>
  );
}

export default ProductOrganizationBoard;

ProductOrganizationBoard.propTypes = {
  opk: PropTypes.string.isRequired,
  ean: PropTypes.string.isRequired,
  permission: PropTypes.func.isRequired,
  searchParamsName: PropTypes.string,
};
